/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { CosmosRichText, ResponsiveContainer, SectionContainer } from "@components/elements/atoms";
import { closeHeadingGapsAcrossBlocks } from "@components/elements/atoms/text/text.utils";
import { CollapsibleRichText } from "@components/elements/molecules/rich-text";
import { richTextContentStyles } from "@components/elements/ref-sections/rich-text";
import { PageLegalContentSectionsFragment } from "@cosmos/hygraph-schema";
import { css, cx } from "@panda/css";
import { VStack } from "@panda/jsx";

const sectionStyles = css({ maxWidth: "100%" });

export const getLegalSections = (
	sections?: PageLegalContentSectionsFragment[] | null,
	isGameMode?: boolean,
	hasHero?: boolean,
): (React.JSX.Element | null | undefined)[] => {
	if (!sections || !sections.length) return [];
	// Authored legal HTML jumps h1→h3 (skips h2) and repeats the doc title as an <h1>, and
	// the doc is often split across several ckText blocks. Normalize ALL blocks as one
	// continuous outline (aria-level only, tags/visuals unchanged); when a hero owns the
	// page h1, re-base the shallowest heading to h2 so the duplicate title collapses to an
	// effective h2 instead of a second h1.
	const baseLevel = hasHero ? 2 : undefined;
	const ckTexts = closeHeadingGapsAcrossBlocks(
		sections.map((section) =>
			section?.__typename === "CosmosElementCkEditorText" ||
			section?.__typename === "CosmosElementCkEditorTextCollapsible"
				? section.ckText
				: null,
		),
		baseLevel,
	);
	return sections.map((section, i) => {
		if (!section || !section.__typename) return;

		switch (section.__typename) {
			case "CosmosElementCkEditorText":
				return (
					<div key={`ck-${i}`} className={cx(richTextContentStyles, sectionStyles)}>
						<CosmosRichText html={ckTexts[i]} isGameMode={isGameMode} />
					</div>
				);

			case "CosmosElementCkEditorTextCollapsible":
				return (
					<CollapsibleRichText
						key={`col-ck-${i}`}
						{...section}
						ckText={ckTexts[i]}
						isGameMode={isGameMode}
						className={sectionStyles}
					/>
				);

			default:
				return null;
		}
	});
};

interface Props {
	sections?: PageLegalContentSectionsFragment[];
	isGameMode?: boolean;
	/** True when a page hero owns the h1 above the legal content. */
	hasHero?: boolean;
}

export const LegalContentSections = ({ sections = [], isGameMode, hasHero }: Props): React.JSX.Element => {
	return (
		<ResponsiveContainer>
			<SectionContainer>
				<VStack id="legal-content-sections" gap="eds.dimension.48" alignItems={"flex-start"}>
					{getLegalSections(sections, isGameMode, hasHero)}
				</VStack>
			</SectionContainer>
		</ResponsiveContainer>
	);
};
